import yaml from 'js-yaml'

/**
 * 模塊加載模式配置
 */
export interface ModuleLoaderConfig {
  useModules: boolean
  fallbackToMonolith: boolean
  moduleLoadOrder?: string[]
}

/**
 * 默認模塊加載順序
 */
const DEFAULT_MODULE_LOAD_ORDER = [
  'common',
  'errors',
  'validation',
  'time',
  'countries',
  'cities',
  'app',
  'navigation',
  'auth',
  'profile',
  'discover',
  'messages',
  'chat',
  'notifications',
  'user',
  'wishTree',
  'settings',
  'subscription',
  'vip',
  'payment',
  'help',
  'legal',
  'feedback'
]

/**
 * 模塊化加載 YAML 翻譯文件
 */
export async function loadModularYamlTranslations(locale: string, config: ModuleLoaderConfig = { useModules: true, fallbackToMonolith: true }): Promise<any> {
  try {
    console.log(`🔍 Loading modular YAML for locale: ${locale}`)
    
    if (!config.useModules) {
      return await loadMonolithYamlTranslations(locale)
    }
    
    const translations: any = {}
    const moduleLoadOrder = config.moduleLoadOrder || DEFAULT_MODULE_LOAD_ORDER
    let loadedModules = 0
    
    // 嘗試加載模塊索引文件
    let moduleIndex: any = null
    try {
      const indexModule = await import(`./locales/modules/index.json`)
      moduleIndex = indexModule.default
      console.log(`📋 Loaded module index, version: ${moduleIndex.version}`)
    } catch (error) {
      console.log(`⚠️  Module index not found, using default load order`)
    }
    
    // 按順序加載模塊
    for (const moduleName of moduleLoadOrder) {
      try {
        const moduleContent = await import(`./locales/modules/${moduleName}/${locale}.yaml?raw`)
        const yamlContent = moduleContent.default
        
        if (yamlContent) {
          const moduleData = yaml.load(yamlContent) as any
          if (moduleData && typeof moduleData === 'object') {
            // 合併模塊數據
            Object.assign(translations, moduleData)
            loadedModules++
            console.log(`  📦 Loaded module: ${moduleName} (${Object.keys(moduleData).length} keys)`)
          }
        }
      } catch (error) {
        console.log(`  ⚠️  Module ${moduleName} not found for locale ${locale}`)
      }
    }
    
    // 檢查是否有其他模塊（不在默認順序中的）
    if (moduleIndex && moduleIndex.modules) {
      const allModules = Object.keys(moduleIndex.modules)
      const extraModules = allModules.filter(module => !moduleLoadOrder.includes(module))
      
      for (const moduleName of extraModules) {
        try {
          const moduleContent = await import(`./locales/modules/${moduleName}/${locale}.yaml?raw`)
          const yamlContent = moduleContent.default
          
          if (yamlContent) {
            const moduleData = yaml.load(yamlContent) as any
            if (moduleData && typeof moduleData === 'object') {
              Object.assign(translations, moduleData)
              loadedModules++
              console.log(`  📦 Loaded extra module: ${moduleName} (${Object.keys(moduleData).length} keys)`)
            }
          }
        } catch (error) {
          console.log(`  ⚠️  Extra module ${moduleName} not found for locale ${locale}`)
        }
      }
    }
    
    console.log(`✅ Modular YAML loaded for ${locale}: ${loadedModules} modules, ${Object.keys(translations).length} total keys`)
    
    // 如果沒有加載到任何模塊且啟用了回退，則嘗試加載單體文件
    if (loadedModules === 0 && config.fallbackToMonolith) {
      console.log(`🔄 No modules loaded, falling back to monolith file for ${locale}`)
      return await loadMonolithYamlTranslations(locale)
    }
    
    return translations
    
  } catch (error) {
    console.error(`❌ Failed to load modular YAML translations for locale ${locale}:`, error)
    
    // 如果啟用了回退，嘗試加載單體文件
    if (config.fallbackToMonolith) {
      console.log(`🔄 Falling back to monolith file for ${locale}`)
      return await loadMonolithYamlTranslations(locale)
    }
    
    return {}
  }
}

/**
 * 加載單體 YAML 翻譯文件（原始方式）
 */
export async function loadMonolithYamlTranslations(locale: string): Promise<any> {
  try {
    console.log(`🔍 Loading monolith YAML for locale: ${locale}`)
    // Use dynamic import with ?raw to get the file content as text
    const yamlModule = await import(`./locales/yaml/${locale}.yaml?raw`)
    const yamlContent = yamlModule.default

    console.log(`✅ Monolith YAML loaded for ${locale}, content length:`, yamlContent?.length)

    // Parse YAML content
    const translations = yaml.load(yamlContent) as any

    console.log(`✅ Monolith YAML parsed for ${locale}, keys:`, Object.keys(translations).slice(0, 10))

    return translations
  } catch (error) {
    console.error(`❌ Failed to load monolith YAML translations for locale ${locale}:`, error)
    return {}
  }
}

/**
 * 向後兼容的 YAML 加載函數
 * @deprecated 使用 loadModularYamlTranslations 替代
 */
export async function loadYamlTranslations(locale: string): Promise<any> {
  // 默認嘗試模塊化加載，如果失敗則回退到單體文件
  return await loadModularYamlTranslations(locale, {
    useModules: true,
    fallbackToMonolith: true
  })
}

/**
 * 加載多個 YAML 翻譯文件（支持模塊化）
 */
export async function loadAllYamlTranslations(
  locales: string[], 
  config: ModuleLoaderConfig = { useModules: true, fallbackToMonolith: true }
): Promise<Record<string, any>> {
  const translations: Record<string, any> = {}

  for (const locale of locales) {
    // Map 'zh' locale to load 'zh-TW.yaml' file
    const fileLocale = locale === 'zh' ? 'zh-TW' : locale
    
    const loadedTranslations = config.useModules 
      ? await loadModularYamlTranslations(fileLocale, config)
      : await loadMonolithYamlTranslations(fileLocale)
      
    translations[locale] = loadedTranslations
    console.log(`✅ Loaded translations for ${locale} from ${fileLocale} (${config.useModules ? 'modular' : 'monolith'})`)
  }

  console.log('📦 Final translations object keys:', Object.keys(translations))
  return translations
}

/**
 * 檢查模塊化支持
 */
export async function checkModularSupport(): Promise<boolean> {
  try {
    // 嘗試加載模塊索引文件
    await import(`./locales/modules/index.json`)
    return true
  } catch (error) {
    return false
  }
}

/**
 * 獲取可用的模塊列表
 */
export async function getAvailableModules(): Promise<string[]> {
  try {
    const indexModule = await import(`./locales/modules/index.json`)
    const moduleIndex = indexModule.default
    return Object.keys(moduleIndex.modules || {})
  } catch (error) {
    console.error('Failed to load module index:', error)
    return []
  }
}